import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp } from "lucide-react";
import { asArray } from "@/lib/api";
import { useFetch } from "@/lib/hooks";

const COLORS = ["#B6FF5C", "#7388FF", "#F6A85C", "#5CD6FF", "#FF7AA8"];
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

export function DownloadTrends() {
  const { data, loading } = useFetch("/admin/download-trends?months=12", null);
  const series = asArray(data?.series);
  const rows = asArray(data?.data).map((r) => ({ ...r, label: monthLabel(r.month) }));
  return (
    <div className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="admin-download-chart-container">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><TrendingUp className="h-4 w-4 text-ph-accent" /> Download trends</p>
          <p className="text-sm text-ph-muted">Top 5 brands by downloads, month by month for the last 12 months. Useful when pitching a spotlight to a brand.</p>
        </div>
        {series.length > 0 && (
          <ul className="flex flex-wrap gap-2" data-testid="download-trends-totals">
            {series.map((s, i) => (
              <li key={s.brand_id} className="rounded-full bg-ph-raised px-3 py-1 font-mono-ph text-[11px] text-ph-ink">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: COLORS[i] }} />{s.name} · {s.total}
              </li>
            ))}
          </ul>
        )}
      </div>
      {loading ? <div className="ph2-shimmer mt-6 h-72 rounded-2xl" /> : series.length === 0 ? (
        <p className="mt-6 rounded-2xl border border-dashed border-ph-line py-16 text-center text-sm text-ph-muted" data-testid="download-trends-empty">
          No downloads recorded in the last 12 months yet. The chart fills in as visitors download price lists.
        </p>
      ) : (
        <div className="mt-6 h-72 w-full" data-testid="download-trends-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="rgb(var(--ph-line))" strokeDasharray="3 6" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "rgb(var(--ph-muted))", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "rgb(var(--ph-muted))", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "rgb(var(--ph-surface))", border: "1px solid rgb(var(--ph-line))", borderRadius: 14, color: "rgb(var(--ph-ink))" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map((s, i) => (
                <Line key={s.brand_id} type="monotone" dataKey={s.brand_id} name={s.name} stroke={COLORS[i]} strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
